import audioBufferToWav from 'audiobuffer-to-wav';
import { voiceError, voiceTrace } from './voiceDiagnostics';

// Native browser decoding plus a proven PCM/WAV encoder. No microphone is
// reopened and no audio leaves the device except through the existing API.
export function pcmRecording(decoded) {
  if (!decoded?.length || !decoded.numberOfChannels || !Number.isFinite(decoded.duration) || decoded.duration <= 0 || decoded.duration > 90) {
    throw voiceError('SAFARI_INVALID_AUDIO', 'I couldn’t read this recording. Please try again.');
  }
  let selected = null;
  for (let channel = 0; channel < decoded.numberOfChannels; channel++) {
    const samples = decoded.getChannelData(channel);
    let mean = 0;
    for (const sample of samples) {
      if (!Number.isFinite(sample)) throw voiceError('SAFARI_INVALID_AUDIO', 'I couldn’t read this recording. Please try again.');
      mean += sample;
    }
    mean /= samples.length;
    let energy = 0, peak = 0;
    for (const sample of samples) { const centered = sample - mean; energy += centered * centered; peak = Math.max(peak, Math.abs(centered)); }
    const rms = Math.sqrt(energy / samples.length);
    if (!selected || rms > selected.rms) selected = { samples, mean, rms, peak, channel };
  }
  // Do not turn digital silence/DC offset into amplified noise or invent text.
  if (selected.rms < 0.000001 || selected.peak < 0.00001) {
    throw Object.assign(voiceError('SAFARI_AUDIO_SILENT', 'No sound was detected. Please check your microphone and try again.'), {
      audioMetadata: { decodedRms: selected.rms, decodedPeak: selected.peak, decodedMean: selected.mean, decodedDurationMs: decoded.duration * 1000, decodedChannels: decoded.numberOfChannels, selectedChannel: selected.channel, sampleRate: decoded.sampleRate },
    });
  }
  const gain = Math.max(1, Math.min(12, 0.85 / selected.peak));
  return {
    rms: selected.rms, peak: selected.peak, mean: selected.mean, channel: selected.channel,
    channels: decoded.numberOfChannels, durationMs: decoded.duration * 1000,
    sampleRate: decoded.sampleRate, gain,
    weak: selected.rms < 0.02 && selected.peak < 0.25 && gain > 1.05,
    toWav(multiplier = 1) {
      // The first WAV retry changes container/channel representation only.
      // A separate weak-signal retry removes DC and applies bounded gain.
      const samples = multiplier === 1 ? selected.samples : Float32Array.from(selected.samples, value => (value - selected.mean) * multiplier);
      const buffer = { numberOfChannels: 1, sampleRate: decoded.sampleRate, getChannelData: () => samples };
      return new Blob([audioBufferToWav(buffer)], { type: 'audio/wav' });
    },
  };
}

export async function decodeSafariRecording(blob, { signal, captureId } = {}) {
  const Offline = window.OfflineAudioContext || window.webkitOfflineAudioContext;
  if (!Offline) throw voiceError('SAFARI_DECODE_UNAVAILABLE', 'I couldn’t process this recording in your browser. Please try again.');
  let timer, abort;
  voiceTrace('safari-decode-start', { captureId, sourceMime: blob.type, sourceBytes: blob.size });
  try {
    const decoder = new Offline(1, 1, 16000);
    const cancelled = new Promise((_, reject) => {
      abort = () => reject(voiceError('TRANSCRIPTION_CANCELLED', 'Recording cancelled.'));
      if (signal?.aborted) abort(); else signal?.addEventListener('abort', abort, { once: true });
      timer = setTimeout(() => reject(voiceError('SAFARI_DECODE_TIMEOUT', 'Processing this recording took too long. Please try again.')), 15000);
    });
    const decoding = blob.arrayBuffer().then(bytes => decoder.decodeAudioData(bytes));
    const decoded = await Promise.race([decoding, cancelled]);
    const pcm = pcmRecording(decoded);
    voiceTrace('safari-audio-decoded', { captureId, decodedRms: pcm.rms, decodedPeak: pcm.peak, decodedMean: pcm.mean, decodedDurationMs: pcm.durationMs, decodedChannels: pcm.channels, selectedChannel: pcm.channel, sampleRate: pcm.sampleRate, gain: pcm.gain });
    return pcm;
  } catch (error) {
    if (error.audioMetadata) voiceTrace('safari-audio-decoded', { captureId, ...error.audioMetadata });
    voiceTrace('safari-decode-error', { captureId, errorName: error.name, errorCode: error.code || 'SAFARI_DECODE_FAILED' });
    if (typeof error.code === 'string') throw error;
    throw voiceError('SAFARI_DECODE_FAILED', 'I couldn’t read this recording. Please try again.');
  } finally { clearTimeout(timer); signal?.removeEventListener('abort', abort); }
}